import { DateTime } from "luxon";
import tools from "../../../helpers/tools";
import ROLES_MANAGER_MIROIR, { estRoleOperateur } from "../../../helpers/rolesManager";

// ============================================================
// Fiche utilisateur — fonctions pures (testables sans DOM) : état du compte,
// libellés, faits d'identité, règles de rattachement organisations / sites.
// ============================================================

export const UTILISATEUR_VIDE = {
  organizations: [],
  sites: [],
  email: "",
  password: "",
  role: "",
  gender: 1,
  firstname: "",
  lastname: "",
  phone: "",
  isEP: false,
  canStartEP: false,
  isDirector: false,
  contractEndAt: "",
  // Compte d'opérateur d'accompagnement (CE-1407) : sa structure et l'option
  // « alertes de planning », à la place des organisations et sites.
  operator: null,
  receivesPlanningAlerts: false,
};

export const estCompteOperateur = (user) => Boolean(user) && estRoleOperateur(user.role);

// Un compte d'opérateur n'a ni organisation ni habilitation EP ; ses sites sont
// ceux de sa structure (évolution du 20/10/2026) : gardés tant que la
// structure ne change pas (`reference` : le compte tel qu'il a été chargé),
// retirés sinon. Un agent Caf ou partenaire n'a pas de structure. L'API
// refuse le mélange.
// Une fois les sites de la structure choisie chargés (`sitesStructure`), ne
// partent que ceux du compte qui en font partie : un compte d'opérateur a au
// moins un site de sa structure (règle du 20/10/2026), et des sites Caf
// cochés avant un changement de rôle ne le suivent pas.
export function corpsEnregistrement(user, reference = {}, sitesStructure = null) {
  if (estCompteOperateur(user)) {
    const memeStructure = Boolean(reference && reference.operator) && reference.operator === user.operator;
    const ids = Array.isArray(sitesStructure) ? sitesStructure.map((s) => s.id) : null;
    const sites = ids ? (user.sites || []).filter((id) => ids.includes(id)) : memeStructure ? user.sites || [] : [];
    // Pas de mot de passe pour un compte d'opérateur : son titulaire le choisit à l'activation (décision du 20/10/2026).
    const { password: _sansMotDePasse, ...sansMotDePasse } = user;
    return {
      ...sansMotDePasse,
      organizations: [],
      sites,
      isEP: false,
      canStartEP: false,
      isDirector: false,
    };
  }
  return { ...user, operator: null, receivesPlanningAlerts: false };
}

export const estCreation = (id) => !id || id === "create";

// Les constantes priment ; en local, constants/BOA ne sert pas ROLES_MANAGER
// et le miroir helpers/rolesManager.js prend le relais (comme le menu).
export function rolesDisponibles(constantes) {
  const roles = constantes && constantes.ROLES_MANAGER;
  return Array.isArray(roles) && roles.length ? roles : ROLES_MANAGER_MIROIR;
}

export function libelleRole(role, constantes) {
  if (!role) return "";
  const trouve = rolesDisponibles(constantes).find((r) => r.id === role);
  return trouve ? trouve.name : role;
}

// « Prénom NOM » sans civilité : la civilité est un champ, pas un titre.
export function nomComplet(user) {
  if (!user) return "";
  const prenom = (user.firstname || "").trim();
  const nom = (user.lastname || "").trim().toUpperCase();
  return [prenom, nom].filter(Boolean).join(" ");
}

// Un compte est activé (isActivated), désactivé (a été activé puis coupé) ou
// en attente d'activation (jamais activé).
export function etatCompte(user) {
  if (!user || !user.id) return null;
  if (user.isActivated) {
    return { id: "active", libelle: "Compte activé", ton: "ok", icone: "fa-check" };
  }
  if (user.activatedAt) {
    return { id: "desactive", libelle: "Compte désactivé", ton: "ko", icone: "fa-power-off" };
  }
  return { id: "attente", libelle: "Activation en attente", ton: "warn", icone: "fa-hourglass-half" };
}

export function contratEchu(user, maintenant = DateTime.local()) {
  if (!user || !user.contractEndAt) return false;
  const fin = DateTime.fromISO(user.contractEndAt);
  return fin.isValid && fin.endOf("day") < maintenant;
}

// Pastilles d'en-tête (trois au plus) : état du compte, rôle, délégation.
export function pastillesUtilisateur(user, constantes) {
  const pastilles = [];
  const etat = etatCompte(user);
  if (etat) pastilles.push({ id: etat.id, ton: etat.ton, icone: etat.icone, libelle: etat.libelle });
  if (user && user.role) {
    pastilles.push({ id: "role", ton: "dark", icone: "fa-id-badge", libelle: libelleRole(user.role, constantes) });
  }
  if (user && user.isDirector) {
    pastilles.push({ id: "delegataire", ton: "info", icone: "fa-stamp", libelle: "Délégataire du DG" });
  }
  if (user && user.receivesPlanningAlerts) {
    pastilles.push({ id: "alertes", ton: "info", icone: "fa-bell", libelle: "Alertes de planning" });
  }
  return pastilles;
}

const dateEtHeure = (valeur) => (valeur ? tools.formatDate(valeur, "dd/MM/yyyy 'à' HH:mm") : "");

export function faitsUtilisateur(user, maintenant = DateTime.local(), { nomStructure = "" } = {}) {
  if (!user) return [];
  const echu = contratEchu(user, maintenant);
  return [
    { id: "email", label: "Email", value: user.email, copyValue: user.email, truncate: true },
    { id: "phone", label: "Téléphone", value: user.phone, copyValue: user.phone, mono: true },
    ...(estCompteOperateur(user) ? [{ id: "structure", label: "Structure", value: nomStructure }] : []),
    { id: "lastConnectionAt", label: "Dernière connexion", value: dateEtHeure(user.lastConnectionAt) },
    {
      id: "contractEndAt",
      label: "Fin de contrat",
      value: user.contractEndAt ? tools.formatDate(user.contractEndAt) : "",
      note: echu ? "Contrat échu" : null,
    },
    { id: "createdAt", label: "Compte créé le", value: user.createdAt ? tools.formatDate(user.createdAt) : "" },
    { id: "activatedAt", label: "Activé le", value: user.activatedAt ? tools.formatDate(user.activatedAt) : "" },
  ];
}

// Valeur d'un <input type="date"> à partir d'une date ISO (vide sinon).
export function versDateSaisie(valeur) {
  if (!valeur) return "";
  const dt = DateTime.fromISO(String(valeur));
  return dt.isValid ? dt.toFormat("yyyy-MM-dd") : "";
}

// --- Organisations et sites ---------------------------------

export const sitesDeOrganisation = (sites = [], organisationId) =>
  sites.filter((s) => s.organization === organisationId);

// Sites cochés qui appartiennent à l'organisation : ils empêchent de la décocher.
export function sitesBloquants(sites = [], organisationId, sitesCoches = []) {
  return sitesDeOrganisation(sites, organisationId)
    .filter((s) => sitesCoches.includes(s.id))
    .map((s) => s.name);
}

export function normaliser(texte) {
  return String(texte || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// Filtre insensible aux accents et à la casse sur le nom du site et sa commune.
export function filtrerSites(sites = [], terme = "") {
  const t = normaliser(terme);
  if (!t) return sites;
  return sites.filter(
    (s) => normaliser(s.name).includes(t) || normaliser(s.location && s.location.name).includes(t)
  );
}

export function libelleCompteSites(selectionnes, total) {
  if (!total) return "Aucun site";
  return `${selectionnes} / ${total} site${total > 1 ? "s" : ""}`;
}

// Un utilisateur est « modifié » quand ses champs diffèrent de la référence
// chargée ; l'ordre des tableaux (organisations, sites) ne compte pas.
const serialiser = (user) =>
  JSON.stringify(
    Object.keys(user || {})
      .sort()
      .reduce((acc, cle) => {
        const valeur = user[cle];
        acc[cle] = Array.isArray(valeur) ? [...valeur].sort() : valeur === null ? "" : valeur;
        return acc;
      }, {})
  );

export function estModifie(user, reference) {
  return serialiser(user) !== serialiser(reference);
}

// Réponse de l'API rabattue sur la forme du formulaire (tableaux présents,
// null → chaîne vide pour les champs de saisie).
export function normaliserUtilisateur(datas = {}) {
  const user = { ...UTILISATEUR_VIDE, ...datas };
  user.organizations = Array.isArray(datas.organizations) ? datas.organizations : [];
  user.sites = Array.isArray(datas.sites) ? datas.sites : [];
  ["email", "firstname", "lastname", "phone", "role", "contractEndAt"].forEach((cle) => {
    if (user[cle] === null || user[cle] === undefined) user[cle] = "";
  });
  user.operator = datas.operator || null;
  user.receivesPlanningAlerts = Boolean(datas.receivesPlanningAlerts);
  delete user.password;
  return user;
}
